
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { ArrowRight, Star, Check, Code, Wrench, Laptop, Users, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { trackButtonClick } from "@/utils/tracking";
import FreeAccessButton from "@/components/premium/FreeAccessButton";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card } from "@/components/ui/card";

export default function Hero() {
  const { user, isSubscribed } = useAuth();
  const [api, setApi] = useState<any>(null);

  useEffect(() => {
    const animateHero = () => {
      const elements = document.querySelectorAll('.hero-animate');
      elements.forEach((el, index) => {
        setTimeout(() => {
          el.classList.add('animate-fade-in');
          el.classList.remove('animate-hidden');
        }, index * 200);
      });
    };
    
    setTimeout(animateHero, 100);
  }, []);

  // Testimonial carousel auto-scroll
  useEffect(() => {
    if (!api) return;

    const interval = setInterval(() => {
      api.scrollNext();
    }, 2000);

    return () => clearInterval(interval);
  }, [api]);

  const handleGetStartedClick = () => {
    trackButtonClick("hero_get_started_button", {
      isSubscribed: isSubscribed,
      userEmail: user?.email
    });
  };
  
  // Testimonials data
  const reviews = [
    {
      quote: "Thank you so much for the time you spent. I gained a lot of insights on what I should do to improve.",
      author: "Meta ML Engineer",
      title: "ML Engineer @ Meta"
    },
    {
      quote: "Coach shared how to structure ML Design interviews for all companies.",
      author: "Meta Candidate",
      title: "ML Candidate @ Meta"
    },
    {
      quote: "Coach has through knowledge on Machine learning. Being on both sides of ML interviews the coach posess strong knowledge on expectations of ML interviews across companies.",
      author: "Meta Candidate",
      title: "ML Candidate @ Meta"
    },
    {
      quote: "Professional and thorough ML system design interview. The coach asked relevant questions to assess understanding and provided specific, actionable feedback for improvement. Clear guidance on how to strengthen system design skills. Highly valuable session",
      author: "Uber ML Candidate",
      title: "ML Candidate @ Uber"
    },
    {
      quote: "Thanks for the great feedback! Immensely helpful discussion and I got a lot of value out of the session esp understanding the expectations around equations, trade-offs and prob formulation at L6/7 level.",
      author: "Senior Staff ML Engineer",
      title: "Senior Staff ML Engineer @ Pinterest, Meta, Stripe"
    },
    {
      quote: "Amazing coach. Very knowledgeable and gave relevant, level specific feedback!",
      author: "Meta E6 ML Candidate",
      title: "E6 ML Candidate @ Meta"
    },
    {
      quote: "I really appreciated how flexible Bharathi was in accommodating my request for a more open-ended discussion of the system design interview process. I gained valuable insights into what's expected at my level and learned which pitfalls to avoid and what goals to focus on to succeed in the interview. I would highly recommend Bharathi for system design preparation.",
      author: "Meta E4 PhD Candidate",
      title: "ML PhD Candidate"
    }
  ];

  return (
    <div className="relative min-h-[80vh] flex items-center overflow-hidden py-12 md:py-24">
      {/* Background decoration */}
      <div className="absolute inset-0 z-0 opacity-10">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-purple-600 blur-3xl"></div>
        <div className="absolute top-1/2 right-0 h-64 w-64 rounded-full bg-indigo-600 blur-3xl"></div>
      </div>
      
      <div className="container relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left column - Text content and testimonials */}
          <div className="text-left">
            <h1 className="font-jakarta text-2xl md:text-4xl font-bold mb-6 hero-animate animate-hidden">
              Master your next <br />
              <span className="text-gradient">ML System Design</span> <br />
              Interview
            </h1>
            <p className="font-sans text-lg md:text-xl text-muted-foreground mb-6 hero-animate animate-hidden">
              Proven frameworks, expert coaching, and comprehensive resources – created by ML engineer from Meta, Airbnb who've interviewed at FAANG companies.
            </p>
            
            {/* Testimonials section moved from Index */}
            <div className="mt-8 mb-8 hero-animate animate-hidden">
              <h3 className="text-lg font-medium mb-3">Trusted by ML Engineers</h3>
              <p className="text-sm text-muted-foreground mb-4">
                See how HelloML has helped engineers land their dream roles at top tech companies.
                <a 
                  href="https://prepfully.com/coaches/K4RRVK" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center ml-2 text-lg font-bold text-blue-600 hover:underline"
                >
                  View coach profile <ExternalLink className="ml-1 h-3 w-3" />
                </a>
              </p>
              
              <Carousel className="w-full hero-animate animate-hidden" setApi={setApi}>
                <CarouselContent>
                  {reviews.map((review, i) => (
                    <CarouselItem key={i}>
                      <div className="p-1">
                        <Card className="p-3 bg-accent/50">
                          <div className="flex gap-1 mb-1 text-yellow-500">
                            <Star className="w-3 h-3 fill-current" />
                            <Star className="w-3 h-3 fill-current" />
                            <Star className="w-3 h-3 fill-current" />
                            <Star className="w-3 h-3 fill-current" />
                            <Star className="w-3 h-3 fill-current" />
                          </div>
                          <blockquote className="text-xs mb-2">{review.quote}</blockquote>
                          <footer className="text-xs font-medium text-muted-foreground">
                            {review.author}
                          </footer>
                        </Card>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
            
            <div className="mt-6 space-y-4 hero-animate animate-hidden">
              {isSubscribed ? (
                <Button size="lg" className="text-base px-8" variant="outline" disabled>
                  You're Already Subscribed <Check className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <Link to="/pricing" onClick={handleGetStartedClick}>
                    <Button size="lg" className="text-base px-8">
                      Get Started <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                  
                  <FreeAccessButton className="w-full sm:w-auto" />
                </div>
              )}
            </div>
            
            <div className="mt-12 flex flex-wrap gap-4 text-sm text-muted-foreground hero-animate animate-hidden">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check mr-2 text-primary"><polyline points="20 6 9 17 4 12"/></svg>
                FAANG interview preparation
              </div>
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check mr-2 text-primary"><polyline points="20 6 9 17 4 12"/></svg>
                Expert-curated content
              </div>
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check mr-2 text-primary"><polyline points="20 6 9 17 4 12"/></svg>
                Practical system design examples
              </div>
            </div>
          </div>

          {/* Right column - Premium value panel */}
          <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-8 shadow-xl hero-animate animate-hidden">
            <div className="space-y-6">
              {/* Featured premium badge */}
              <div className="flex justify-center">
                <div className="bg-primary/10 text-primary px-4 py-2 rounded-full inline-flex items-center">
                  <Star className="h-4 w-4 mr-2 fill-primary" />
                  <span className="font-medium">Premium Membership</span>
                </div>
              </div>
              
              {/* Trust Badges */}
              <div className="flex justify-center gap-4 p-4 bg-primary/5 rounded-lg">
                <div className="text-center">
                  <div className="font-bold">50+</div>
                  <div className="text-sm text-muted-foreground">ML Interviews</div>
                </div>
                <div className="text-center">
                  <div className="font-bold">31K+</div>
                  <div className="text-sm text-muted-foreground">
                    <a 
                      href="https://medium.com/p/78a2882a05c5" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-primary transition-colors"
                    >
                      Medium Reads
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Feature highlights */}
              <div className="space-y-3 p-4 rounded-lg border bg-card">
                <h3 className="font-medium">What you'll get:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">6+ in-depth ML system design case studies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Wrench className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">Architecture templates tailored for Senior & Staff-level engineers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Users className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">1:1 Coaching with a FAANG interviewer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Code className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">[Coming Soon] ML fundamentals series to build strong intuition</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Laptop className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">[Coming Soon] Interactive coding playground to test your ideas</span>
                  </li>
                </ul>
              </div>
              
              {/* CTA buttons */}
              {isSubscribed ? (
                <Button className="w-full" size="lg" variant="outline" disabled>
                  You're Already Subscribed <Check className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <div className="space-y-3">
                  <Link 
                    to="/pricing"
                    className="block w-full"
                    onClick={handleGetStartedClick}
                  >
                    <Button className="w-full" size="lg" variant="default">
                      Get Premium for $29 for 2 months
                    </Button>
                  </Link>
                  
                  <FreeAccessButton className="w-full" />
                </div>
              )}
              
              <div className="text-center text-sm text-muted-foreground">
                <div>Your Shortcut to Cracking ML System Design Interviews</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
